import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Chip,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  CreditCard as PaymentIcon,
  Search as SearchIcon,
  Houseboat as BoatIcon,
  BeachAccess as BeachIcon,
  LocalGroceryStore as GroceryIcon,
  Icecream as SnackIcon,
  SportsBar as DrinkIcon,
  PersonSearch as CustomerIcon
} from '@mui/icons-material';
import { POSProvider } from '../../contexts/POSContext';
import POSLayout from '../../layouts/POSLayout';

interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  description?: string;
  sku: string;
  inventory: number;
  imagePath?: string;
}

interface CartItem extends Product {
  quantity: number;
  notes: string;
}

const PRODUCTS: Product[] = [
  { id: '1', name: 'Marina T-Shirt', price: 24.99, category: 'apparel', description: 'Cotton T-shirt with marina logo', sku: 'APP001', inventory: 25, imagePath: '/images/tshirt.jpg' },
  { id: '2', name: 'Marina Hat', price: 19.99, category: 'apparel', description: 'Adjustable cap with marina logo', sku: 'APP002', inventory: 15, imagePath: '/images/hat.jpg' },
  { id: '3', name: 'Sunglasses', price: 29.99, category: 'apparel', description: 'Polarized sunglasses', sku: 'APP003', inventory: 10, imagePath: '/images/sunglasses.jpg' },
  { id: '4', name: 'Beach Towel', price: 34.99, category: 'beach', description: 'Large cotton beach towel', sku: 'BCH001', inventory: 20, imagePath: '/images/towel.jpg' },
  { id: '5', name: 'Sunscreen SPF 50', price: 12.99, category: 'beach', description: 'Waterproof sunscreen lotion', sku: 'BCH002', inventory: 30, imagePath: '/images/sunscreen.jpg' },
  { id: '6', name: 'Beach Chair', price: 49.99, category: 'beach', description: 'Folding beach chair', sku: 'BCH003', inventory: 8, imagePath: '/images/chair.jpg' },
  { id: '7', name: 'Life Jacket', price: 69.99, category: 'boating', description: 'Coast guard approved life vest', sku: 'BOT001', inventory: 15, imagePath: '/images/lifejacket.jpg' },
  { id: '8', name: 'Boat Cleaner', price: 14.99, category: 'boating', description: 'All-purpose boat cleaner', sku: 'BOT002', inventory: 25, imagePath: '/images/cleaner.jpg' },
  { id: '9', name: 'Rope - 50ft', price: 29.99, category: 'boating', description: 'Marine grade rope', sku: 'BOT003', inventory: 12, imagePath: '/images/rope.jpg' },
  { id: '10', name: 'Chips', price: 3.99, category: 'snacks', description: 'Regular potato chips', sku: 'SNK001', inventory: 40, imagePath: '/images/chips.jpg' },
  { id: '11', name: 'Chocolate Bar', price: 2.49, category: 'snacks', description: 'Milk chocolate', sku: 'SNK002', inventory: 50, imagePath: '/images/chocolate.jpg' },
  { id: '12', name: 'Trail Mix', price: 4.99, category: 'snacks', description: 'Nuts and dried fruit mix', sku: 'SNK003', inventory: 35, imagePath: '/images/trailmix.jpg' },
  { id: '13', name: 'Water Bottle', price: 1.99, category: 'drinks', description: 'Purified water, 16oz', sku: 'DRK001', inventory: 60, imagePath: '/images/water.jpg' },
  { id: '14', name: 'Soda', price: 2.49, category: 'drinks', description: 'Assorted flavors, 12oz', sku: 'DRK002', inventory: 48, imagePath: '/images/soda.jpg' },
  { id: '15', name: 'Sports Drink', price: 3.49, category: 'drinks', description: 'Electrolyte drink, 20oz', sku: 'DRK003', inventory: 32, imagePath: '/images/sportdrink.jpg' }
];

const RetailPOSContent: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<string>('apparel');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    setSearchTerm('');
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const addToCart = (product: Product) => {
    const existingItemIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingItemIndex >= 0) {
      // Item already in cart, increase quantity
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      // Add new item to cart
      setCart([...cart, { ...product, quantity: 1, notes: '' }]);
    }
  };
  
  const removeFromCart = (id: string) => {
    const existingItemIndex = cart.findIndex(item => item.id === id);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].quantity > 1) {
        // Decrease quantity
        updatedCart[existingItemIndex].quantity -= 1;
      } else {
        // Remove item completely
        updatedCart.splice(existingItemIndex, 1);
      }
      setCart(updatedCart);
    }
  };
  
  const deleteFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };
  
  const updateItemNotes = (id: string, notes: string) => {
    setCart(cart.map(item => 
      item.id === id ? { ...item, notes } : item
    ));
  };
  
  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  const calculateTax = (subtotal: number) => {
    return subtotal * 0.0725; // 7.25% tax rate
  };
  
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };
  
  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }
    
    alert(`Sale completed! Total: $${calculateTotal().toFixed(2)}`);
    // In a real app, this would process payment and update inventory
    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
  };
  
  // Filter products by category and search term
  const filteredProducts = PRODUCTS.filter(product => {
    const matchesCategory = activeTab === product.category;
    const matchesSearch = searchTerm 
      ? product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    
    return matchesCategory && matchesSearch;
  });
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        Retail POS
      </Typography>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {/* Left side: Products */}
        <Grid xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <TextField
                placeholder="Search products..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ mr: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            
            <Tabs
              value={activeTab}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<BeachIcon />} label="Apparel" value="apparel" iconPosition="start" />
              <Tab icon={<BeachIcon />} label="Beach" value="beach" iconPosition="start" />
              <Tab icon={<BoatIcon />} label="Boating" value="boating" iconPosition="start" />
              <Tab icon={<SnackIcon />} label="Snacks" value="snacks" iconPosition="start" />
              <Tab icon={<DrinkIcon />} label="Drinks" value="drinks" iconPosition="start" />
            </Tabs>
            
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {filteredProducts.map((product) => (
                  <Grid
                    key={product.id}
                    xs={12}
                    sm={6}
                    md={4}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          SKU: {product.sku} | Stock: {product.inventory}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {product.description}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          ${product.price.toFixed(2)}
                        </Typography>
                      </CardContent>
                      <Box sx={{ p: 1 }}>
                        <Button 
                          fullWidth 
                          variant="contained" 
                          startIcon={<AddIcon />}
                          onClick={() => addToCart(product)}
                          disabled={product.inventory <= 0}
                        >
                          Add to Cart
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
                
                {filteredProducts.length === 0 && (
                  <Grid xs={12}>
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No products found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
        
        {/* Right side: Cart */}
        <Grid
          xs={12}
          md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <CartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Shopping Cart
              </Typography>
              <Badge badgeContent={cart.length} color="primary">
                <CartIcon />
              </Badge>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Customer Name"
                fullWidth
                size="small"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CustomerIcon />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Customer Phone"
                fullWidth
                size="small"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {cart.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ my: 4, textAlign: 'center' }}>
                Cart is empty
              </Typography>
            ) : (
              <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                {cart.map((item) => (
                  <React.Fragment key={item.id}>
                    <ListItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <IconButton size="small" onClick={() => removeFromCart(item.id)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body1" sx={{ mx: 1 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton size="small" onClick={() => addToCart(item)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <ListItemText 
                        primary={item.name} 
                        secondary={
                          <>
                            <Typography variant="body2">
                              ${item.price.toFixed(2)} each
                            </Typography>
                            {item.notes && (
                              <Typography variant="body2" color="text.secondary">
                                Note: {item.notes}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="body1">
                            ${(item.price * item.quantity).toFixed(2)}
                          </Typography>
                          <IconButton edge="end" size="small" onClick={() => deleteFromCart(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <TextField
                      placeholder="Add notes..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={item.notes}
                      onChange={(e) => updateItemNotes(item.id, e.target.value)}
                      sx={{ mx: 2, mb: 1, width: 'calc(100% - 32px)' }}
                    />
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">${calculateSubtotal().toFixed(2)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Tax (7.25%):</Typography>
                <Typography variant="body1">${calculateTax(calculateSubtotal()).toFixed(2)}</Typography>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">${calculateTotal().toFixed(2)}</Typography>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<PaymentIcon />}
                onClick={handleCheckout}
                disabled={cart.length === 0}
              >
                Complete Sale
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const RetailPOS: React.FC = () => {
  return (
    <POSProvider>
      <POSLayout>
        <RetailPOSContent />
      </POSLayout>
    </POSProvider>
  );
};

export default RetailPOS; 